import { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, Loader2, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AuthModal } from "@/components/AuthModal";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { useUnlock } from "@/hooks/useUnlock";
import { supabase } from "@/integrations/supabase/client";
import { saveAuthReturn, consumeAuthIntent, clearAuthReturn } from "@/lib/authReturn";
import type { User } from "@supabase/supabase-js";

interface Props {
  purchaseType: "premium_guide" | "digital_resource";
  entityId: string | null | undefined;
  title?: string;
  description?: string;
  perks?: string[];
  children: ReactNode; // unlocked content
}

const ONE_TIME_PRICE_ID: Record<Props["purchaseType"], string> = {
  premium_guide: "premium_guide_unlock_v2",
  digital_resource: "digital_resource_unlock_v2",
};

const ONE_TIME_LABEL: Record<Props["purchaseType"], string> = {
  premium_guide: "Unlock this Premium Guide",
  digital_resource: "Unlock this Resource",
};

export function PremiumUnlockGate({
  purchaseType,
  entityId,
  title = "Premium content",
  description = "Unlock this with a one-time purchase or get All-Access for everything on RunoBlog.",
  perks,
  children,
}: Props) {
  const [user, setUser] = useState<User | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<"one-time" | "subscribe" | null>(null);
  const { openCheckout, checkoutDialog } = useStripeCheckout();
  const { isUnlocked, loading } = useUnlock(user, purchaseType, entityId ?? null);
  const resumedRef = useRef(false);

  const triggerCheckout = (kind: "one-time" | "subscribe") => {
    if (kind === "one-time") {
      if (!entityId) return;
      openCheckout({
        priceId: ONE_TIME_PRICE_ID[purchaseType],
        purchaseType,
        entityId,
        title: ONE_TIME_LABEL[purchaseType],
      });
    } else {
      openCheckout({ priceId: "all_access_monthly", title: "Subscribe to All-Access" });
    }
  };

  // Resume premium intent saved before an auth redirect (OAuth or email).
  const tryResumeIntent = (u: User | null) => {
    if (!u || !entityId || resumedRef.current) return;
    const intent = consumeAuthIntent(
      (i) =>
        i.type === "premium_unlock" &&
        i.purchaseType === purchaseType &&
        i.entityId === entityId,
    );
    if (intent && intent.type === "premium_unlock") {
      resumedRef.current = true;
      // Defer slightly so any scroll restoration completes first.
      setTimeout(() => triggerCheckout(intent.kind), 400);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      tryResumeIntent(u);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      const u = s?.user ?? null;
      setUser(u);
      if (u && pendingAction) {
        const action = pendingAction;
        setPendingAction(null);
        triggerCheckout(action);
        return;
      }
      tryResumeIntent(u);
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAction, entityId, purchaseType]);

  const handle = (kind: "one-time" | "subscribe") => {
    if (!user) {
      setPendingAction(kind);
      if (entityId) {
        saveAuthReturn({
          type: "premium_unlock",
          purchaseType,
          entityId,
          kind,
        });
      } else {
        saveAuthReturn();
      }
      setAuthOpen(true);
      return;
    }
    triggerCheckout(kind);
  };

  if (loading) {
    return (
      <Card className="p-12 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </Card>
    );
  }

  if (isUnlocked) return <>{children}</>;

  const defaultPerks = perks ?? [
    "Instant access to this premium content",
    "Yours forever — no recurring charges",
    "Or get All-Access and unlock everything on RunoBlog",
  ];

  return (
    <>
      <Card className="p-8 border-primary/30 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="flex items-center gap-2 mb-4">
          <Badge variant="secondary" className="gap-1">
            <Lock className="h-3 w-3" /> Premium
          </Badge>
        </div>
        <h3 className="text-2xl font-bold mb-2">{title}</h3>
        <p className="text-muted-foreground mb-6">{description}</p>

        <ul className="space-y-2 mb-8">
          {defaultPerks.map((p) => (
            <li key={p} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
              <span>{p}</span>
            </li>
          ))}
        </ul>

        <div className="grid sm:grid-cols-2 gap-3">
          <Button size="lg" onClick={() => handle("one-time")} disabled={!entityId}>
            <Lock className="mr-2 h-4 w-4" />
            Unlock for $3
          </Button>
          <Button size="lg" variant="outline" onClick={() => handle("subscribe")}>
            <Sparkles className="mr-2 h-4 w-4" />
            All-Access — $9.99/mo
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-4 text-center">
          See all plans on the{" "}
          <Link to="/pricing" className="underline hover:text-primary">
            Pricing page
          </Link>
          .
        </p>
      </Card>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      {checkoutDialog}
    </>
  );
}
